import { useEffect, useRef, useState, useMemo } from "react";
import { api } from "../api/client";
import { useFrameworks, FRAMEWORK_LABELS } from "../context/FrameworksContext";
import type { FrameworkId } from "../context/FrameworksContext";
import { useThemeMode } from "../context/ThemeContext";
import { frameworkColor } from "../nav/frameworkMeta";
import { theme, card, pageContainer, pageHeading, pageSubtitle } from "../theme";
import { DimensionHeading, RulerProgress } from "../components/Drawing";
import { SEVERITY_META, SEVERITY_ORDER, severityOf } from "../lib/riskScoring";

// ── Types ─────────────────────────────────────────────────────────────────

interface FrameworkScore {
  id: string;
  /** 0-100, or null when the framework has no scoreable control set (NIST CSF, IWA 48, GDPR, UK GDPR). */
  score: number | null;
  implemented: number;
  total: number;
}

interface AnalyticsSummary {
  overallScore: number;
  controlsImplemented: number;
  controlsTotal: number;
  openRisks: number;
  riskBreakdown: { critical: number; high: number; medium: number; low: number };
  riskRating: string;
  movement30d: { raised: number; resolved: number };
  nextAuditDays: number | null;
  nextAuditLabel: string | null;
  frameworks: FrameworkScore[];
}

interface SectorCohort {
  callerPercentile: number | null;
  medianScore: number;
  cohortCount: number;
  cohortLabel: string;
}

interface FrameworkBenchmark {
  frameworkId: string;
  callerScore: number | null;
  callerPercentile: number | null;
  medianScore: number;
  p25Score: number;
  p75Score: number;
  cohortCount: number;
  cohortLabel: string;
  sectorCohort: SectorCohort | null;
}

// Framework identity colours come from nav/frameworkMeta.ts (the sidebar
// dots), so chart lines and cards share the sidebar's identity.

const RING_CIRC = 2 * Math.PI * 32; // r=32, viewBox 80×80

// ── Score history types ───────────────────────────────────────────────────

interface ScoreHistoryPoint {
  date: string; // "YYYY-MM-DD"
  overallScore: number;
  frameworkScores: Record<string, number>;
}

// Build a 365-element array of daily scores from sparse history records.
// Gaps are forward-filled from the previous known value; days before the
// first record carry the first recorded value.
function buildDailySeries(
  history: ScoreHistoryPoint[],
  getValue: (p: ScoreHistoryPoint) => number,
  n: number,
): number[] {
  if (history.length === 0) return [];

  const byDate = new Map(history.map((h) => [h.date, getValue(h)]));
  const now = new Date();
  const result: number[] = [];

  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    result.push(byDate.get(key) ?? NaN);
  }

  // Forward-fill: carry earliest known value backwards, then carry forward
  const firstKnown = result.find((v) => !isNaN(v)) ?? 0;
  let last = firstKnown;
  for (let i = 0; i < result.length; i++) {
    if (isNaN(result[i])) result[i] = last;
    else last = result[i];
  }

  return result;
}

// ── Canvas chart ──────────────────────────────────────────────────────────

interface ChartSeries {
  label: string;
  fwId: string | null; // null = Overall
  color: string;
  data: number[];
}

function drawChart(
  canvas: HTMLCanvasElement,
  series: ChartSeries[],
  days: number,
  hovIdx: number,
  selected: Set<string>,
  isDark: boolean,
) {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const W = rect.width || 600;
  const H = 220;
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  canvas.style.width = W + "px";
  canvas.style.height = H + "px";

  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);

  const root = document.documentElement;
  const g = (v: string) => getComputedStyle(root).getPropertyValue(v).trim();

  const surface  = g("--c-panel");
  const gridC    = g("--c-border");
  const mutedC   = g("--c-muted");

  ctx.fillStyle = surface;
  ctx.fillRect(0, 0, W, H);

  const Pl = 36, Pr = 54, Pt = 10, Pb = 28;
  const cW = W - Pl - Pr;
  const cH = H - Pt - Pb;
  const yMin = 30, yMax = 100;

  function xAt(i: number) { return Pl + (i / (days - 1)) * cW; }
  function yAt(v: number) { return Pt + cH - ((v - yMin) / (yMax - yMin)) * cH; }

  // Grid + Y labels
  ctx.font = `10px ${theme.font}`;
  ctx.textAlign = "right";
  for (const v of [40, 50, 60, 70, 80, 90, 100]) {
    const y = yAt(v);
    ctx.strokeStyle = gridC; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(Pl, y); ctx.lineTo(Pl + cW, y); ctx.stroke();
    ctx.fillStyle = mutedC; ctx.fillText(v + "%", Pl - 4, y + 3.5);
  }

  // X labels
  const refDate = new Date();
  const nL = days <= 30 ? 4 : days <= 90 ? 5 : 7;
  ctx.textAlign = "center"; ctx.fillStyle = mutedC;
  for (let li = 0; li <= nL; li++) {
    const idx = Math.round((li / nL) * (days - 1));
    const d = new Date(refDate); d.setDate(d.getDate() - (days - 1 - idx));
    ctx.fillText(
      d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      xAt(idx), H - 5,
    );
  }

  const hasSel = selected.size > 0;

  function emphasis(fwId: string | null) {
    if (!hasSel || fwId === null) return 1;
    return selected.has(fwId) ? 1 : 0.08;
  }

  // Draw ghosts first, then full-emphasis lines
  for (const pass of [false, true]) {
    for (const s of series) {
      const tail = s.data.slice(-days);
      const em = emphasis(s.fwId);
      const full = em > 0.5;
      if (pass !== full) continue;

      ctx.strokeStyle = full
        ? s.color
        : isDark ? "rgba(180,180,180,0.15)" : "rgba(30,30,30,0.10)";
      ctx.lineWidth = full ? 2 : 1.5;
      ctx.lineJoin = "round"; ctx.lineCap = "round";
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(xAt(0), yAt(tail[0]));
      for (let i = 1; i < days; i++) ctx.lineTo(xAt(i), yAt(tail[i]));
      ctx.stroke();
    }
  }

  // Hover crosshair + dots
  if (hovIdx >= 0 && hovIdx < days) {
    const hx = xAt(hovIdx);
    ctx.strokeStyle = mutedC; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(hx, Pt); ctx.lineTo(hx, Pt + cH); ctx.stroke();
    ctx.setLineDash([]);
    for (const s of series) {
      if (emphasis(s.fwId) < 0.5) continue;
      const hy = yAt(s.data.slice(-days)[hovIdx]);
      ctx.beginPath(); ctx.arc(hx, hy, 6, 0, Math.PI * 2); ctx.fillStyle = surface; ctx.fill();
      ctx.beginPath(); ctx.arc(hx, hy, 4, 0, Math.PI * 2); ctx.fillStyle = s.color; ctx.fill();
    }
  }

  // Endpoint labels with collision avoidance
  const fullSeries = series.filter((s) => emphasis(s.fwId) > 0.5);
  const endpoints = fullSeries
    .map((s) => ({ s, val: s.data[s.data.length - 1], markY: yAt(s.data[s.data.length - 1]) }))
    .sort((a, b) => a.markY - b.markY);

  const labelY = endpoints.map((e) => e.markY);
  const minGap = 13;
  for (let i = 1; i < labelY.length; i++) {
    if (labelY[i] - labelY[i - 1] < minGap) labelY[i] = labelY[i - 1] + minGap;
  }
  const maxY = Pt + cH - 4;
  const lastLY = labelY[labelY.length - 1] ?? 0;
  if (lastLY > maxY) {
    const shift = lastLY - maxY;
    for (let i = 0; i < labelY.length; i++) labelY[i] -= shift;
  }

  const ex = xAt(days - 1);
  endpoints.forEach((ep, mi) => {
    ctx.beginPath(); ctx.arc(ex, ep.markY, 6, 0, Math.PI * 2); ctx.fillStyle = surface; ctx.fill();
    ctx.beginPath(); ctx.arc(ex, ep.markY, 5, 0, Math.PI * 2); ctx.fillStyle = ep.s.color; ctx.fill();
    if (Math.abs(labelY[mi] - ep.markY) > 3) {
      ctx.strokeStyle = ep.s.color; ctx.lineWidth = 0.8; ctx.setLineDash([2, 2]);
      ctx.beginPath(); ctx.moveTo(ex + 5, ep.markY); ctx.lineTo(ex + 6, labelY[mi] + 1); ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.fillStyle = ep.s.color;
    ctx.font = `bold 11px ${theme.font}`; ctx.textAlign = "left";
    ctx.fillText(ep.val.toFixed(0) + "%", ex + 8, labelY[mi] + 4);
  });
}

// ── Benchmark subcomponents ───────────────────────────────────────────────

function BenchmarkRangeBar({
  p25, median, p75, caller, color,
}: {
  p25: number; median: number; p75: number; caller: number | null; color: string;
}) {
  return (
    <div style={{ position: "relative", height: 24, margin: "10px 0 28px" }}>
      {/* Background track */}
      <div style={{
        position: "absolute", left: 0, right: 0, top: "50%",
        height: 4, background: "var(--c-border)", borderRadius: 2,
        transform: "translateY(-50%)",
      }} />
      {/* IQR shaded band (P25 → P75) */}
      <div style={{
        position: "absolute",
        left: `${p25}%`, width: `${p75 - p25}%`,
        top: "50%", height: 10, borderRadius: 3,
        background: color, opacity: 0.22,
        transform: "translateY(-50%)",
      }} />
      {/* Median tick */}
      <div style={{
        position: "absolute",
        left: `${median}%`, top: "50%",
        width: 2, height: 14, borderRadius: 1,
        background: color, opacity: 0.65,
        transform: "translate(-50%, -50%)",
      }} />
      {/* Caller diamond */}
      {caller !== null && (
        <div style={{ position: "absolute", left: `${caller}%`, top: "50%", transform: "translate(-50%, -50%)" }}>
          <div style={{
            width: 10, height: 10, borderRadius: 2, background: color,
            transform: "rotate(45deg)",
            boxShadow: `0 0 0 2px var(--c-panel), 0 0 0 3.5px ${color}`,
          }} />
        </div>
      )}
      {/* Labels below bar */}
      <div style={{ position: "absolute", left: `${p25}%`, top: "100%", marginTop: 6, transform: "translateX(-50%)", fontSize: 9, color: "var(--c-muted)", whiteSpace: "nowrap" }}>25th pct</div>
      <div style={{ position: "absolute", left: `${median}%`, top: "100%", marginTop: 6, transform: "translateX(-50%)", fontSize: 9, color: "var(--c-muted)", whiteSpace: "nowrap" }}>Median</div>
      <div style={{ position: "absolute", left: `${p75}%`, top: "100%", marginTop: 6, transform: "translateX(-50%)", fontSize: 9, color: "var(--c-muted)", whiteSpace: "nowrap" }}>75th pct</div>
      {caller !== null && (
        <div style={{ position: "absolute", left: `${caller}%`, top: "100%", marginTop: 6, transform: "translateX(-50%)", fontSize: 9, color, fontWeight: 700, whiteSpace: "nowrap" }}>You</div>
      )}
    </div>
  );
}

function BenchmarkCard({ bm }: { bm: FrameworkBenchmark }) {
  const color = frameworkColor(bm.frameworkId);
  const fwLabel = FRAMEWORK_LABELS[bm.frameworkId as FrameworkId] ?? bm.frameworkId;
  const topPct = bm.callerPercentile !== null ? 100 - bm.callerPercentile : null;

  return (
    <div style={card}>
      {/* Heading row */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-heading)" }}>{fwLabel}</span>
        {bm.callerScore !== null && (
          <span style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: "-0.5px" }}>
            {bm.callerScore}%
          </span>
        )}
      </div>

      {/* Range bar */}
      <BenchmarkRangeBar
        p25={bm.p25Score} median={bm.medianScore} p75={bm.p75Score}
        caller={bm.callerScore} color={color}
      />

      {/* Percentile copy */}
      {bm.callerPercentile !== null && topPct !== null && (
        <div style={{ fontSize: 12, color: "var(--c-muted)", lineHeight: 1.5 }}>
          {topPct <= 50
            ? `You're in the top ${topPct}% of organisations using ${fwLabel}`
            : `You're ahead of ${bm.callerPercentile}% of organisations using ${fwLabel}`}
        </div>
      )}

      <div style={{ fontSize: 11, color: "var(--c-faint)", marginTop: 5 }}>
        Based on {bm.cohortCount.toLocaleString()} organisations
      </div>

      {/* Sector sub-cohort */}
      {bm.sectorCohort && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--c-border)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--c-muted)", marginBottom: 3 }}>
            {bm.sectorCohort.cohortLabel} sector ({bm.sectorCohort.cohortCount} orgs)
          </div>
          <div style={{ fontSize: 11, color: "var(--c-muted)" }}>
            Sector median: <strong>{bm.sectorCohort.medianScore}%</strong>
            {bm.sectorCohort.callerPercentile !== null && (
              <> · You're ahead of <strong>{bm.sectorCohort.callerPercentile}%</strong> in sector</>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────────────

function KpiTile({ label, value, unit, sub, subColor }: {
  label: string; value: string; unit?: string; sub: string; subColor?: string;
}) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--c-muted)", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1 }}>
        {value}
        {unit && <span style={{ fontSize: 17, fontWeight: 500, color: "var(--c-muted)" }}>{unit}</span>}
      </div>
      <div style={{ fontSize: 12, marginTop: 6, color: subColor ?? "var(--c-muted)" }}>{sub}</div>
    </div>
  );
}

function RiskBar({ label, count, max, color }: { label: string; count: number; max: number; color: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "var(--c-muted)" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
          {label}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--c-subtle)", fontVariantNumeric: "tabular-nums" }}>{count}</span>
      </div>
      <div style={{ height: 5, background: "var(--c-border)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${max > 0 ? (count / max) * 100 : 0}%`, background: color, borderRadius: 3, transition: "width 0.4s" }} />
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export function AnalyticsPage() {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const { enrolled } = useFrameworks();

  const [riskMatrix, setRiskMatrix] = useState<{ matrix: number[][]; total: number; unscored: number } | null>(null);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [history, setHistory] = useState<ScoreHistoryPoint[]>([]);
  const [benchmarks, setBenchmarks] = useState<FrameworkBenchmark[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(90);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hovIdx, setHovIdx] = useState(-1);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [tooltipDate, setTooltipDate] = useState("");
  const [tooltipRows, setTooltipRows] = useState<{ label: string; color: string; val: string }[]>([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const animRef = useRef(0);

  useEffect(() => {
    Promise.all([
      api.get<AnalyticsSummary>("/analytics/summary"),
      api.get<{ history: ScoreHistoryPoint[] }>("/analytics/score-history?days=365"),
      api.get<{ matrix: number[][]; total: number; unscored: number }>("/analytics/risk-matrix").catch(() => null),
      api.get<{ benchmarks: FrameworkBenchmark[] }>("/analytics/benchmarks").catch(() => null),
    ])
      .then(([s, h, rm, bm]) => {
        setSummary(s);
        setHistory(h.history);
        if (rm) setRiskMatrix(rm);
        setBenchmarks(bm ? bm.benchmarks : null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const hasRealHistory = history.length >= 2;

  // Build chart series from real score history. Empty when history is still accumulating.
  const chartSeries = useMemo<ChartSeries[]>(() => {
    if (!summary || !hasRealHistory) return [];
    const N = 365;
    // The canvas cannot read a CSS variable, so take the palette's accent as it stands now (isDark re-runs this on a theme change).
    const accentColor = getComputedStyle(document.documentElement).getPropertyValue("--c-accent").trim() || (isDark ? "#c21a9b" : "#b31385");

    const series: ChartSeries[] = [
      {
        label: "Overall",
        fwId: null,
        color: accentColor,
        data: buildDailySeries(history, (p) => p.overallScore, N),
      },
    ];

    for (const fw of summary.frameworks) {
      if (!enrolled.has(fw.id as FrameworkId)) continue;
      series.push({
        label: FRAMEWORK_LABELS[fw.id as FrameworkId] ?? fw.id,
        fwId: fw.id,
        color: frameworkColor(fw.id),
        data: buildDailySeries(history, (p) => p.frameworkScores[fw.id] ?? 0, N),
      });
    }
    return series;
  }, [summary, enrolled, isDark]);

  // Animate in on load / range change
  useEffect(() => {
    if (!canvasRef.current || chartSeries.length === 0) return;
    cancelAnimationFrame(rafRef.current);
    animRef.current = 0;
    const t0 = performance.now();
    const dur = 700;
    function frame(now: number) {
      animRef.current = Math.min((now - t0) / dur, 1);
      if (canvasRef.current) {
        // Draw only up to animated fraction of data points
        const visibleDays = Math.max(2, Math.round(range * (1 - Math.pow(1 - animRef.current, 3))));
        const slicedSeries = chartSeries.map((s) => ({
          ...s,
          data: s.data.slice(-range).slice(0, visibleDays),
        }));
        // Pad to range length so xAt math stays consistent
        const paddedSeries = slicedSeries.map((s) => ({
          ...s,
          data: [...Array(range - s.data.length).fill(s.data[0] ?? 0), ...s.data],
        }));
        drawChart(canvasRef.current, paddedSeries, range, -1, selected, isDark);
      }
      if (animRef.current < 1) rafRef.current = requestAnimationFrame(frame);
    }
    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [chartSeries, range, selected, isDark]);

  // Redraw on hover
  useEffect(() => {
    if (!canvasRef.current || animRef.current < 1 || chartSeries.length === 0) return;
    drawChart(canvasRef.current, chartSeries.map((s) => ({ ...s, data: s.data.slice(-range) })), range, hovIdx, selected, isDark);
  }, [hovIdx, selected, isDark, chartSeries, range]);

  // Resize observer
  useEffect(() => {
    if (!canvasRef.current) return;
    const ro = new ResizeObserver(() => {
      if (!canvasRef.current || chartSeries.length === 0) return;
      drawChart(canvasRef.current, chartSeries.map((s) => ({ ...s, data: s.data.slice(-range) })), range, hovIdx, selected, isDark);
    });
    ro.observe(canvasRef.current.parentElement!);
    return () => ro.disconnect();
  }, [chartSeries, range, hovIdx, selected, isDark]);

  function handleCanvasMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas || animRef.current < 1) return;
    const rect = canvas.getBoundingClientRect();
    const Pl = 36, Pr = 54;
    const usableW = rect.width - Pl - Pr;
    const frac = (e.clientX - rect.left - Pl) / usableW;
    const idx = Math.round(frac * (range - 1));
    if (idx < 0 || idx >= range) { setHovIdx(-1); setTooltipPos(null); return; }

    setHovIdx(idx);
    const ref = new Date(); ref.setDate(ref.getDate() - (range - 1 - idx));
    setTooltipDate(ref.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }));

    const hasSel = selected.size > 0;
    const rows = chartSeries
      .filter((s) => !hasSel || s.fwId === null || selected.has(s.fwId))
      .map((s) => ({
        label: s.label,
        color: s.color,
        val: (s.data.slice(-range)[idx]?.toFixed(1) ?? "—") + "%",
      }));
    setTooltipRows(rows);

    const ttW = 170;
    const tx = e.clientX + 14 + ttW > window.innerWidth - 8 ? e.clientX - ttW - 14 : e.clientX + 14;
    setTooltipPos({ x: tx, y: e.clientY - 60 });
  }

  function handleCanvasMouseLeave() {
    setHovIdx(-1);
    setTooltipPos(null);
  }

  function toggleFramework(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(summary?.frameworks.map((f) => f.id) ?? []));
  }

  function deselectAll() {
    setSelected(new Set());
  }

  function exportCsv() {
    if (history.length === 0) return;
    const fwIds = summary?.frameworks.map((f) => f.id) ?? [];
    const header = ["Date", "Overall", ...fwIds.map((id) => FRAMEWORK_LABELS[id as FrameworkId] ?? id)];
    const rows = history.map((h) => [
      h.date,
      h.overallScore,
      ...fwIds.map((id) => h.frameworkScores[id] ?? ""),
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const maxRisk = summary
    ? Math.max(1, summary.riskBreakdown.critical + summary.riskBreakdown.high + summary.riskBreakdown.medium + summary.riskBreakdown.low)
    : 1;

  // The register's severity colours (lib/riskScoring.ts), so a band reads the same on every page.
  const ratingColor = summary?.riskRating === "Critical" ? SEVERITY_META.critical.text
    : summary?.riskRating === "High" ? SEVERITY_META.high.text
    : summary?.riskRating === "Medium" ? SEVERITY_META.medium.text
    : SEVERITY_META.low.text;

  const hasSel = selected.size > 0;

  if (loading) {
    return (
      <div style={{ ...pageContainer, color: "var(--c-muted)", fontSize: 13 }}>
        Loading analytics…
      </div>
    );
  }

  const enrolledFwScores = (summary?.frameworks ?? []).filter((f) => enrolled.has(f.id as FrameworkId));
  const fwCols = Math.min(enrolledFwScores.length || 1, 4);

  return (
    <div style={pageContainer}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
        <div>
          <h1 style={pageHeading}>Progress</h1>
          <p style={pageSubtitle}>
            {summary
              ? `${enrolledFwScores.length} standard${enrolledFwScores.length !== 1 ? "s" : ""} · updated just now`
              : "No data yet"}
          </p>
        </div>
        {/* Controls row */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {history.length > 0 && (
          <button
            onClick={exportCsv}
            style={{
              background: "none", border: "1px solid var(--c-border)", borderRadius: 3,
              padding: "5px 13px", fontSize: 12, color: "var(--c-muted)", cursor: "pointer",
              fontFamily: theme.font, display: "flex", alignItems: "center", gap: 5,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M6.5 1v7M4 6l2.5 2.5L9 6M1 10v1a1 1 0 001 1h9a1 1 0 001-1v-1" /></svg>
            Export CSV
          </button>
        )}
        {/* Range selector */}
        <div style={{ display: "flex", gap: 2, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", borderRadius: 3, padding: 3 }}>
          {([30, 90, 180, 365] as const).map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              style={{
                background: range === d ? "var(--c-accent)" : "none",
                border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: range === d ? 700 : 500,
                color: range === d ? "#fff" : "var(--c-muted)",
                padding: "4px 12px", borderRadius: 3,
                transition: "background 0.12s, color 0.12s",
                fontFamily: theme.font,
              }}
            >
              {d === 30 ? "30D" : d === 90 ? "90D" : d === 180 ? "6M" : "1Y"}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "flex", gap: 11, marginBottom: 14, flexWrap: "wrap" }}>
        <KpiTile
          label="Overall Score"
          value={String(summary?.overallScore ?? 0)}
          unit="%"
          sub={summary?.overallScore != null ? (summary.overallScore >= 70 ? "On track" : "Needs attention") : "—"}
          subColor={summary && summary.overallScore >= 70 ? "var(--c-successText)" : "var(--c-warningText)"}
        />
        <KpiTile
          label="Controls Implemented"
          value={String(summary?.controlsImplemented ?? 0)}
          unit={summary ? ` / ${summary.controlsTotal}` : ""}
          sub={summary ? `${Math.round((summary.controlsImplemented / Math.max(1, summary.controlsTotal)) * 100)}% complete` : "—"}
        />
        <KpiTile
          label="Open Risks"
          value={String(summary?.openRisks ?? 0)}
          sub={summary ? `${summary.riskBreakdown.critical} Critical · ${summary.riskBreakdown.high} High` : "—"}
          subColor={summary && (summary.riskBreakdown.critical > 0 || summary.riskBreakdown.high > 0) ? "var(--c-dangerText)" : undefined}
        />
        <KpiTile
          label="Next Audit"
          value={summary?.nextAuditDays != null ? String(summary.nextAuditDays) : "—"}
          unit={summary?.nextAuditDays != null ? "d" : undefined}
          sub={summary?.nextAuditLabel ?? "No audit scheduled"}
        />
      </div>

      {/* Main grid: trend chart + risk panel */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 11, marginBottom: 14 }}>

        {/* Trend chart */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Compliance Score Trend</span>
            <span style={{ fontSize: 12, color: "var(--c-muted)" }}>
              {hasRealHistory ? "Daily snapshots" : "Collecting data — check back tomorrow"}
            </span>
          </div>
          {!hasRealHistory && (
            <div style={{ fontSize: 12, color: "var(--c-muted)", background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", borderRadius: 3, padding: "8px 12px", marginBottom: 10 }}>
              Historical tracking started today. The trend chart will populate as daily snapshots are recorded.
            </div>
          )}
          {/* Legend */}
          <div style={{ display: "flex", gap: 14, marginBottom: 10, flexWrap: "wrap" }}>
            {chartSeries.map((s) => {
              const faded = hasSel && s.fwId !== null && !selected.has(s.fwId);
              return (
                <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--c-muted)", opacity: faded ? 0.3 : 1, transition: "opacity 0.2s" }}>
                  <div style={{ width: 16, height: 2, borderRadius: 1, background: s.color, flexShrink: 0 }} />
                  {s.label}
                </div>
              );
            })}
          </div>
          <canvas
            ref={canvasRef}
            height={220}
            style={{ display: "block", width: "100%" }}
            onMouseMove={handleCanvasMouseMove}
            onMouseLeave={handleCanvasMouseLeave}
          />
        </div>

        {/* Risk panel */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Risk Register</span>
            <span style={{ fontSize: 12, color: "var(--c-muted)" }}>by severity</span>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 16 }}>
            <span style={{ fontSize: 40, fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1 }}>{summary?.openRisks ?? 0}</span>
            <span style={{ fontSize: 13, color: "var(--c-muted)" }}>open risks</span>
          </div>
          {SEVERITY_ORDER.map((sev) => (
            <RiskBar key={sev} label={SEVERITY_META[sev].label} count={summary?.riskBreakdown[sev] ?? 0} max={maxRisk} color={SEVERITY_META[sev].text} />
          ))}

          <div style={{ borderTop: "1px solid var(--c-border)", margin: "14px 0" }} />

          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--c-muted)", marginBottom: 8 }}>
            30-day movement
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--c-muted)", padding: "2px 0" }}>
            <span>New risks raised</span>
            <span style={{ fontWeight: 600, color: summary?.movement30d.raised ? "var(--c-dangerText)" : "var(--c-muted)" }}>
              {summary?.movement30d.raised != null ? `+${summary.movement30d.raised}` : "—"}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--c-muted)", padding: "2px 0" }}>
            <span>Risks resolved</span>
            <span style={{ fontWeight: 600, color: summary?.movement30d.resolved ? "var(--c-successText)" : "var(--c-muted)" }}>
              {summary?.movement30d.resolved != null ? `−${summary.movement30d.resolved}` : "—"}
            </span>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--c-border)", fontSize: 12, fontWeight: 600, color: "var(--c-muted)" }}>
            <span>Overall risk rating</span>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 2, background: ratingColor, color: "#fff" }}>
              {summary?.riskRating ?? "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Framework cards */}
      {enrolledFwScores.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <DimensionHeading count={`${enrolledFwScores.length} enrolled`} style={{ margin: 0, fontSize: 13, flex: 1 }}>Standards</DimensionHeading>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={selectAll}
                style={{ background: "none", border: "1px solid var(--c-border)", borderRadius: 3, padding: "4px 11px", fontSize: 12, color: "var(--c-muted)", cursor: "pointer", fontFamily: theme.font }}
              >
                Select all
              </button>
              <button
                onClick={deselectAll}
                style={{ background: "none", border: "1px solid var(--c-border)", borderRadius: 3, padding: "4px 11px", fontSize: 12, color: "var(--c-muted)", cursor: "pointer", fontFamily: theme.font }}
              >
                Deselect all
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: `repeat(${fwCols}, 1fr)`, gap: 11 }}>
            {enrolledFwScores.map((fw) => {
              const col = frameworkColor(fw.id);
              const isSel = selected.has(fw.id);
              const isFaded = hasSel && !isSel;
              const offset = (RING_CIRC * (1 - (fw.score ?? 0) / 100)).toFixed(1);
              const fwLabel = FRAMEWORK_LABELS[fw.id as FrameworkId] ?? fw.id;

              return (
                <div
                  key={fw.id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSel}
                  onClick={() => toggleFramework(fw.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleFramework(fw.id); } }}
                  style={{
                    ...card,
                    display: "flex", flexDirection: "column", alignItems: "center",
                    textAlign: "center", gap: 10, cursor: "pointer",
                    opacity: isFaded ? 0.25 : 1,
                    borderColor: isSel ? col : undefined,
                    boxShadow: isSel ? `0 0 0 1.5px ${col}` : undefined,
                    transform: isFaded ? "scale(0.985)" : "scale(1)",
                    transition: "opacity 0.2s, border-color 0.15s, box-shadow 0.15s, transform 0.15s",
                    outline: "none",
                    userSelect: "none",
                  }}
                >
                  {/* Ring */}
                  <div style={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}>
                    <svg viewBox="0 0 80 80" width={80} height={80} style={{ transform: "rotate(-90deg)" }} aria-hidden>
                      <circle cx="40" cy="40" r="32" fill="none" stroke="var(--c-border)" strokeWidth={7} />
                      <circle
                        cx="40" cy="40" r="32" fill="none" stroke={col} strokeWidth={7}
                        strokeDasharray={RING_CIRC.toFixed(1)}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ fontSize: fw.score === null ? 14 : 18, fontWeight: 700, letterSpacing: "-0.4px", color: fw.score === null ? "var(--c-muted)" : "var(--c-heading)" }}>
                        {fw.score === null ? "n/a" : `${fw.score}%`}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-heading)" }}>{fwLabel}</div>
                  </div>

                  {/* Mini bar */}
                  <div style={{ width: "100%" }}>
                    <RulerProgress value={fw.score ?? 0} fill={col} label={`${fwLabel} score`} height={10} style={{ marginBottom: 4 }} />
                    <div style={{ fontSize: 11, color: "var(--c-muted)", fontVariantNumeric: "tabular-nums" }}>
                      {fw.score === null ? "Not scored" : `${fw.implemented} / ${fw.total} items`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk heat map */}
      {riskMatrix && riskMatrix.total > 0 && (
        <div style={{ ...card, marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Risk Heat Map</span>
              <span style={{ marginLeft: 10, fontSize: 12, color: "var(--c-muted)" }}>Residual likelihood × impact · open risks only</span>
            </div>
            {riskMatrix.unscored > 0 && (
              <span style={{ fontSize: 11, color: "var(--c-faint)" }}>{riskMatrix.unscored} risk{riskMatrix.unscored !== 1 ? "s" : ""} unscored</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 0, alignItems: "flex-end" }}>
            {/* Y axis label */}
            <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 10, color: "var(--c-muted)", paddingRight: 6, textAlign: "center", letterSpacing: "0.05em" }}>
              LIKELIHOOD →
            </div>
            <div style={{ flex: 1 }}>
              {/* Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 3, marginBottom: 4 }}>
                {[4, 3, 2, 1, 0].map((li) =>
                  [0, 1, 2, 3, 4].map((ii) => {
                    const count = riskMatrix.matrix[li]?.[ii] ?? 0;
                    const score = (li + 1) * (ii + 1); // 1–25
                    // The platform's bands (low 1–4, medium 5–9, high 10–12, critical 15–25), in theme colours.
                    const meta = SEVERITY_META[severityOf(score)];
                    return (
                      <div
                        key={`${li}-${ii}`}
                        title={`L${li + 1} × I${ii + 1}: ${count} risk${count !== 1 ? "s" : ""}`}
                        style={{
                          aspectRatio: "1",
                          borderRadius: 4,
                          background: meta.bg,
                          opacity: count > 0 ? 1 : 0.4,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, fontWeight: count > 0 ? 700 : 400,
                          color: count > 0 ? "var(--c-heading)" : "var(--c-border)",
                          transition: "background 0.2s",
                          cursor: count > 0 ? "default" : undefined,
                        }}
                      >
                        {count > 0 ? count : ""}
                      </div>
                    );
                  })
                )}
              </div>
              {/* X axis labels */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 3 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <div key={n} style={{ textAlign: "center", fontSize: 10, color: "var(--c-muted)" }}>I{n}</div>
                ))}
              </div>
              <div style={{ textAlign: "center", fontSize: 10, color: "var(--c-muted)", letterSpacing: "0.05em", marginTop: 2 }}>IMPACT →</div>
            </div>
          </div>
          {/* Legend */}
          <div style={{ display: "flex", gap: 16, marginTop: 14, fontSize: 11, color: "var(--c-muted)", flexWrap: "wrap" }}>
            {SEVERITY_ORDER.map((sev) => ({ bg: SEVERITY_META[sev].bg, label: `${SEVERITY_META[sev].label} (${SEVERITY_META[sev].range})` })).map((z) => (
              <span key={z.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: z.bg, display: "inline-block", border: "1px solid var(--c-line)" }} />
                {z.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Peer Benchmarking */}
      <div style={{ ...card, marginTop: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Peer Benchmarking</span>
          <span style={{ fontSize: 12, color: "var(--c-muted)" }}>How your compliance compares</span>
        </div>
        {loading ? (
          <div style={{ fontSize: 12, color: "var(--c-muted)" }}>Loading…</div>
        ) : benchmarks === null ? (
          <div style={{ fontSize: 12, color: "var(--c-muted)" }}>Unable to load benchmark data.</div>
        ) : benchmarks.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--c-muted)", lineHeight: 1.6, maxWidth: 560 }}>
            Benchmarking data will appear once enough organisations are using the same frameworks — check back as the platform grows.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(benchmarks.length, 3)}, 1fr)`, gap: 11 }}>
            {benchmarks.map((bm) => (
              <BenchmarkCard key={bm.frameworkId} bm={bm} />
            ))}
          </div>
        )}
      </div>

      {/* Hover tooltip */}
      {tooltipPos && tooltipRows.length > 0 && (
        <div style={{
          position: "fixed", left: tooltipPos.x, top: tooltipPos.y,
          background: isDark ? "#0E0B22" : "#0f0520",
          color: isDark ? "#EAE6FA" : "#EAE6FA",
          border: "1px solid var(--c-border)",
          borderRadius: 3, padding: "10px 12px", pointerEvents: "none",
          zIndex: 999, minWidth: 160,
          boxShadow: "var(--c-sheetShadow)",
          fontFamily: theme.font, fontSize: 12,
        }}>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>{tooltipDate}</div>
          {tooltipRows.map((r) => (
            <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: "1.5px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, opacity: 0.85 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
                <span>{r.label}</span>
              </div>
              <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.val}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
